// The one prompt for both situations. Mounted once at the app root; it never
// replaces page content, so an edited form beneath it keeps its state.
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, RefreshCw, X } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { currentHolds, subscribeToHolds, summarise, holdReason } from './dirtyRegistry';
import {
  planRecovery, readAttempts, noteAttempt, clearAttempts, cooldownRemainingMs, type Trigger,
} from './recoveryState';
import { runUpdate } from './updateCoordinator';
import {
  activateWaiting, countClients, getWaitingWorker, onWaitingChange, pollOtherTabs,
} from './registerWorker';

export const RecoveryPrompt: React.FC<{ chunkFailed?: boolean }> = ({ chunkFailed = false }) => {
  const { t } = useTranslation('common');
  const [waiting, setWaiting] = useState<boolean>(() => getWaitingWorker() !== null);
  const [, bump] = useState(0);
  const [busy, setBusy] = useState(false);
  const [deferred, setDeferred] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [online, setOnline] = useState(() => navigator.onLine);

  useEffect(() => onWaitingChange(() => setWaiting(getWaitingWorker() !== null)), []);
  useEffect(() => subscribeToHolds(() => bump((n) => n + 1)), []);
  useEffect(() => {
    const on = () => setOnline(true); const off = () => setOnline(false);
    window.addEventListener('online', on); window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  const trigger: Trigger | null = chunkFailed ? 'chunk-error' : waiting ? 'worker-waiting' : null;

  // ⚠️ An attempt only counts as a FAILURE if the problem is still here
  // after the reload. Without this the counter grew on every click —
  // including the clicks that worked — and clearAttempts was never called
  // anywhere in the app. Each trigger clears independently: a chunk that
  // failed to load says nothing about whether a worker update will.
  useEffect(() => {
    if (!chunkFailed) clearAttempts('chunk-error');
    if (!waiting) clearAttempts('worker-waiting');
  }, [chunkFailed, waiting]);

  // While paused, re-render as the cooldown runs down so the countdown in
  // the message does not sit frozen at its first value.
  useEffect(() => {
    if (!trigger) return undefined;
    const id = window.setInterval(() => bump((n) => n + 1), 15_000);
    return () => window.clearInterval(id);
  }, [trigger]);

  const holds = summarise(currentHolds());
  const plan = trigger && planRecovery(
    { trigger, online, unsaved: holds.unsaved, pending: holds.pending,
      otherTabsBlocking: false,
      reloadAttempts: readAttempts(trigger),
      cooldownRemainingMs: cooldownRemainingMs(trigger) },
    holdReason(holds), deferred,
  );

  const reload = useCallback(async () => {
    if (!trigger) return;
    setBusy(true);
    setDeferred(null);
    try {
      if (trigger === 'worker-waiting') {
        const out = await runUpdate({
          countClients, poll: pollOtherTabs, activate: activateWaiting,
          reload: () => { noteAttempt(trigger); window.location.reload(); },
        });
        if (out.kind === 'deferred') setDeferred(out.reason);
      } else {
        noteAttempt(trigger);
        window.location.reload();
      }
    } finally { setBusy(false); }
  }, [trigger]);

  if (!trigger || !plan || dismissed) return null;

  return (
    // Nonmodal by construction: no backdrop, no focus trap, anchored to one
    // corner so the page stays usable and reachable behind it.
    <div role="status" aria-live="polite"
         className="fixed bottom-4 right-4 z-50 w-[min(26rem,calc(100vw-2rem))] rounded-xl border border-border bg-card p-4 shadow-lg"
         data-testid="recovery-prompt">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">{plan.title}</p>
          <p className="mt-1 text-sm text-muted-foreground">{plan.body}</p>
          {plan.blockedReason && (
            <p className="mt-2 text-sm text-warning" data-testid="recovery-blocked">{plan.blockedReason}</p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" onClick={reload} disabled={!plan.canReload || busy} className="gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              {t('recovery.reload', 'Reload app')}
            </Button>
            {/* Always dismissible, including a chunk failure. An
                undismissable prompt can trap the very work it protects:
                someone with unsaved edits elsewhere needs to reach the save
                button. The broken section keeps its own persistent recovery
                action, so dismissing this loses nothing. */}
            <Button size="sm" variant="outline" onClick={() => setDismissed(true)}>
              {plan.updateKnown ? t('recovery.later', 'Later') : t('recovery.notNow', 'Not now')}
            </Button>
          </div>
        </div>
        <button type="button" onClick={() => setDismissed(true)} aria-label={t('dismiss', 'Dismiss') as string}
                className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted">
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  );
};
